import { motion } from 'motion/react'
import { useMe } from '../../api/hooks'
import type { HomeBriefing, StandingResponse, SummaryKPIs, UserProfileOut } from '../../api/types'
import { Avatar } from '../Avatar'
import { AsyncPanel, PanelSkeleton, Shimmer } from '../ui/AsyncPanel'
import { Panel, TOKENS } from '../ui/dashboard-primitives'
import { useHeroPanel } from './panelStates'
import { StandingLedger } from './StandingLedger'
import { UpNextCard } from './UpNextCard'
import { WeekStrip } from './WeekStrip'
import { YamiKuro } from './YamiKuro'
import { cn } from '../../lib/utils'

const CREAM = TOKENS.cream
const AMBER = TOKENS.amber

function formatToday(): string {
  return new Date()
    .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function getGreeting(): string {
  const h = new Date().getHours()
  if (h < 12) return 'Morning'
  if (h < 17) return 'Afternoon'
  return 'Evening'
}

/**
 * Section 01, the standing. It draws only once all four of its sources are in,
 * and it used to stand on its skeleton until they were: on an outage, forever,
 * which reads as "still loading" rather than "failed". Through `AsyncPanel`
 * (Spec 2 D5, #170), a failed source now reads as one.
 */
export function HeroBanner() {
  const hero = useHeroPanel()

  return (
    // The error note carries no section padding of its own; the hero's does.
    <div className={cn(hero.state === 'error' && 'py-10 lg:py-16')}>
      <AsyncPanel
        sources={hero.sources}
        hasContent={hero.hasContent}
        subject="Your standing"
        skeleton={<HeroSkeleton />}
      >
        {() => hero.data && <HeroContent {...hero.data} />}
      </AsyncPanel>
    </div>
  )
}

function HeroContent({
  profile,
  summary,
  standing,
  briefing,
}: {
  profile: UserProfileOut
  summary: SummaryKPIs
  standing: StandingResponse
  briefing: HomeBriefing
}) {
  const { data: me } = useMe()

  return (
    <section className="relative py-10 lg:py-16">
      <div
        aria-hidden
        className="absolute -inset-x-8 -inset-y-12 -z-10 pointer-events-none"
        style={{
          background:
            // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
            'radial-gradient(60% 80% at 70% 40%, rgba(232,197,160,0.09), transparent 70%)',
        }}
      />

      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-[10px] tracking-[0.32em] uppercase"
        // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
        style={{ fontFamily: 'var(--font-mono)', color: 'rgba(245, 237, 225, 0.55)' }}
      >
        {formatToday()} · Week {summary.training_weeks}
      </motion.p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[7fr_5fr] gap-x-18 gap-y-12 items-start">
        {/* Left: the address + the ledger. */}
        <div className="flex flex-col">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-4 sm:gap-6.5 mb-8 lg:mb-10.5"
          >
            {/*
              The hero's own amber ring + glow wraps `<Avatar>` rather than
              living inside it -- `<Avatar>` carries only the cream hairline
              every avatar gets (Avatar.tsx), and this site keeps its own
              heavier treatment on top. 100px total, border-box: a 2px border
              leaves a 96px content box, which is exactly the size handed to
              `<Avatar>` so the photo/monogram fills it with no gap.
            */}
            <span
              aria-hidden="true"
              className="rounded-full shrink-0 overflow-hidden"
              style={{
                width: 100,
                height: 100,
                background: TOKENS.warmDark,
                border: `2px solid ${AMBER}`,
                boxShadow:
                  // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
                  '0 0 22px -4px rgba(232,197,160,0.30), 0 18px 36px -16px rgba(20,14,12,0.6)',
              }}
            >
              {/* `userId={me?.user_id ?? 0}` keyed the avatar blob on 0,
                  which is a SHARED key across every account on this browser
                  -- precisely the hole `['avatar', userId]` was chosen to
                  close (api/hooks.ts). Nothing is rendered until `me`
                  resolves rather than falling back to 0. The ring around
                  this holds the space, and the branch is unreachable in
                  practice anyway: RequireFirstRun renders null until ['me']
                  has resolved, and it gates every page in the shell. */}
              {me?.user_id != null && (
                <Avatar
                  first={profile.first_name}
                  last={profile.last_name}
                  userId={me.user_id}
                  size={96}
                />
              )}
            </span>
            <h2
              className="leading-[0.95]"
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 500,
                fontSize: 'clamp(2.375rem, 5vw, 4.25rem)',
                letterSpacing: '-0.035em',
                color: CREAM,
              }}
            >
              {getGreeting()},
              <br />
              <span style={{ color: AMBER }}>{profile.first_name}.</span>
            </h2>
          </motion.div>

          <StandingLedger standing={standing} />
        </div>

        {/* Right: the week (with the mascot) + the prescription. */}
        <div className="flex flex-col">
          {/* The mascot takes a fixed 150px. It shows only when the card itself is
              wide enough to keep the week readable beside it (a container query, not a
              viewport one): in the two-column layout from lg up to about 1340px the
              card is narrow, and '1fr auto' left the seven-day grid 116px, so its 21px
              labels overlapped (measured at 1100px). At @sm (24rem inside the card)
              the week keeps at least ~216px. The grid is an inner element because a
              container query styles the container's descendants, never the container. */}
          <Panel variant="light" className="@container">
            <div className="grid grid-cols-1 items-center gap-x-4.5 @sm:grid-cols-[minmax(0,1fr)_auto]">
              <div className="min-w-0">
                <WeekStrip days={briefing.week_strip} />
              </div>
              <YamiKuro width={150} className="hidden @sm:block" />
            </div>
          </Panel>

          {briefing.due_session && <UpNextCard session={briefing.due_session} />}
        </div>
      </div>
    </section>
  )
}

function HeroSkeleton() {
  return (
    <PanelSkeleton label="Loading your standing">
      <section className="relative py-10 lg:py-16">
        <Shimmer height={12} width={192} radius={4} />
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[7fr_5fr] gap-x-18 gap-y-12">
          <div>
            <div className="flex items-center gap-6.5 mb-10.5">
              <Shimmer height={100} width={100} radius={999} />
              <Shimmer height={56} width={224} radius={4} />
            </div>
            <Shimmer height={96} width={160} radius={4} />
            <div className="mt-8 max-w-155">
              <Shimmer height={80} radius={4} />
            </div>
          </div>
          <div>
            <Shimmer height={112} radius={24} />
            <div className="mt-10">
              <Shimmer height={192} radius={24} />
            </div>
          </div>
        </div>
      </section>
    </PanelSkeleton>
  )
}
